import React from 'react'
import {Document, Font, Image, Page, StyleSheet, Text, View} from '@react-pdf/renderer'
import moment from 'moment'
import logo from '../../../_websoft/assets/images/logo.jpg'
import {ECommerceLead} from '../../models/e-commerce'

Font.register({
  family: 'Roboto',
  fonts: [
    {src: require('../../../_websoft/fonts/Roboto/Roboto-Regular.ttf'), fontWeight: 'normal'},
    {src: require('../../../_websoft/fonts/Roboto/Roboto-Bold.ttf'), fontWeight: 'bold'},
  ],
})

const colors = {
  primary: '#1f2937',
  border: '#d1d5db',
  section: '#e5e7eb',
  light: '#f9fafb',
  text: '#374151',
  muted: '#6b7280',
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 24,
    paddingHorizontal: 28,
    fontFamily: 'Roboto',
    fontSize: 9,
    color: colors.text,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `2 solid ${colors.primary}`,
    paddingBottom: 10,
    marginBottom: 14,
  },
  logo: {
    width: 130,
  },
  headerInfo: {
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 3,
  },
  subtitle: {
    fontSize: 8,
    color: colors.muted,
  },
  sectionTitle: {
    backgroundColor: colors.section,
    color: colors.primary,
    fontSize: 10,
    fontWeight: 'bold',
    padding: 6,
    marginBottom: 6,
  },
  table: {
    border: `1 solid ${colors.border}`,
    marginBottom: 14,
  },
  row: {
    flexDirection: 'row',
    borderBottom: `1 solid ${colors.border}`,
  },
  lastRow: {
    flexDirection: 'row',
  },
  label: {
    width: '30%',
    backgroundColor: colors.light,
    borderRight: `1 solid ${colors.border}`,
    paddingTop: 5,
    paddingBottom: 5,
    paddingHorizontal: 6,
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.muted,
  },
  value: {
    width: '70%',
    paddingTop: 5,
    paddingBottom: 5,
    paddingHorizontal: 6,
    lineHeight: 1.25,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 28,
    right: 28,
    borderTop: `1 solid ${colors.border}`,
    paddingTop: 4,
    textAlign: 'center',
    fontSize: 7,
    color: '#9ca3af',
  },
})

type PdfField = {
  key: keyof ECommerceLead
  label: string
  date?: boolean
}

const fields: PdfField[] = [
  {key: 'id', label: 'ID'},
  {key: 'company_name', label: 'Naziv kompanije'},
  {key: 'official_company_name', label: 'Zvanični naziv kompanije'},
  {key: 'website', label: 'Web stranica'},
  {key: 'country', label: 'Država'},
  {key: 'country_code', label: 'Kod države'},
  {key: 'city', label: 'Grad'},
  {key: 'region', label: 'Regija'},
  {key: 'industry', label: 'Industrija'},
  {key: 'lead_type', label: 'Tip potencijalnog klijenta'},
  {key: 'why_relevant', label: 'Zašto je relevantan'},
  {key: 'buying_signal', label: 'Signal kupovine'},
  {key: 'signal_date', label: 'Datum signala', date: true},
  {key: 'signal_status', label: 'Status signala'},
  {key: 'potential_product_need', label: 'Potencijalna potreba za proizvodom'},
  {key: 'product_need_status', label: 'Status potrebe za proizvodom'},
  {key: 'project_name', label: 'Naziv projekta'},
  {key: 'project_location', label: 'Lokacija projekta'},
  {key: 'project_stage', label: 'Faza projekta'},
  {key: 'project_value', label: 'Vrijednost projekta'},
  {key: 'project_size', label: 'Veličina projekta'},
  {key: 'investor_facility_owner', label: 'Investitor / vlasnik objekta'},
  {key: 'main_contractor', label: 'Glavni izvođač'},
  {key: 'other_project_participants', label: 'Ostali učesnici projekta'},
  {key: 'contact_person', label: 'Kontakt osoba'},
  {key: 'contact_role', label: 'Uloga kontakta'},
  {key: 'email', label: 'E-mail'},
  {key: 'phone', label: 'Telefon'},
  {key: 'linkedin', label: 'LinkedIn'},
  {key: 'general_company_contact', label: 'Opšti kontakt kompanije'},
  {key: 'lead_score', label: 'Ocjena potencijalnog klijenta'},
  {key: 'confidence_score', label: 'Ocjena pouzdanosti'},
  {key: 'priority', label: 'Prioritet'},
  {key: 'verified_facts', label: 'Provjerene činjenice'},
  {key: 'inferences', label: 'Zaključci'},
  {key: 'unknown_information', label: 'Nepoznate informacije'},
  {key: 'research_notes', label: 'Istraživačke napomene'},
  {key: 'source_urls', label: 'Izvorni URL-ovi'},
  {key: 'source_evidence', label: 'Dokazi izvora'},
  {key: 'found_at', label: 'Pronađeno', date: true},
  {key: 'last_checked', label: 'Posljednja provjera', date: true},
  {key: 'research_country_agent', label: 'Istraživač po državi'},
  {key: 'research_run_id', label: 'ID istraživanja'},
  {key: 'offer_status', label: 'Status ponude'},
  {key: 'created_at', label: 'Kreirano', date: true},
  {key: 'updated_at', label: 'Ažurirano', date: true},
]

const formatValue = (value: unknown, date = false) => {
  if (value === undefined || value === null || value === '') {
    return '-'
  }

  if (date) {
    const parsedDate = moment(String(value))
    return parsedDate.isValid() ? parsedDate.format('DD-MM-YYYY') : String(value)
  }

  if (Array.isArray(value)) {
    return value.join(', ')
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}

type Props = {
  lead: ECommerceLead
}

const PotencijalniKlijentiPdf: React.FC<Props> = ({lead}) => {
  return (
    <Document>
      <Page size='A4' style={styles.page} wrap>
        <View style={styles.header} fixed>
          <Image src={logo} style={styles.logo} />
          <View style={styles.headerInfo}>
            <Text style={styles.title}>Potencijalni klijent</Text>
            <Text style={styles.subtitle}>E-Commerce lead detalji</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>PODACI O POTENCIJALNOM KLIJENTU</Text>
        <View style={styles.table}>
          {fields.map((field, index) => (
            <View key={field.key} style={index === fields.length - 1 ? styles.lastRow : styles.row} wrap={false}>
              <Text style={styles.label}>{field.label}</Text>
              <Text style={styles.value}>{formatValue(lead[field.key], field.date)}</Text>
            </View>
          ))}
        </View>

        <Text style={styles.footer} fixed>
          Alternativa d.o.o. - E-Commerce
        </Text>
      </Page>
    </Document>
  )
}

export default PotencijalniKlijentiPdf
